import { useToast } from '@chakra-ui/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FEATURE_HSM_RECONTACT_TEMPLATE } from '@/constants/feature-flags';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useAppDispatch, useAppSelector } from '@/hooks/use-redux';
import { UserContext } from '@/services/auth/user-context-provider';
import {
  dismissRecontactTemplate,
  getRecontactTemplate,
  submitRecontactTemplate,
} from '@/services/hsm-backend/recontact-template';
import { RecontactTemplateState } from '@/services/hsm-backend/types';
import { CommonState } from '@/store/slices/common/common.types';
import { fetchTemplatesAction } from '@/store/slices/templates/template.async-actions';

interface RecontactTemplateContextValue {
  state: RecontactTemplateState | null;
  isModalOpen: boolean;
  isSubmitting: boolean;
  openModal: () => void;
  closeModal: () => void;
  submit: (text: string) => Promise<void>;
}

export const RecontactTemplateContext =
  createContext<RecontactTemplateContextValue>({
    state: null,
    isModalOpen: false,
    isSubmitting: false,
    openModal: () => undefined,
    closeModal: () => undefined,
    submit: () => Promise.resolve(),
  });

export const useRecontactTemplate = () => useContext(RecontactTemplateContext);

export const RecontactTemplateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const enabled = useFeatureFlag(FEATURE_HSM_RECONTACT_TEMPLATE);
  const { currentChannel } = useAppSelector<CommonState>(
    (state) => state.common
  );
  const { websites } = useContext(UserContext);
  const lineActive =
    websites?.find((website) => website.id === currentChannel)
      ?.hsmLineStatus === 'active';

  const [state, setState] = useState<RecontactTemplateState | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setState(null);
    setIsModalOpen(false);
    if (!enabled || !lineActive || !currentChannel) {
      return;
    }

    let cancelled = false;

    getRecontactTemplate(currentChannel)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState(result);
        setIsModalOpen(result.shouldOffer);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [enabled, lineActive, currentChannel]);

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    if (!state?.shouldOffer || !currentChannel) {
      return;
    }
    dismissRecontactTemplate(currentChannel)
      .then(() =>
        setState(
          (current) =>
            current && { ...current, shouldOffer: false, dismissed: true }
        )
      )
      .catch(() => undefined);
  }, [state, currentChannel]);

  const submit = useCallback(
    async (text: string) => {
      if (!currentChannel) {
        return;
      }
      setIsSubmitting(true);
      try {
        setState(await submitRecontactTemplate(currentChannel, text));
        setIsModalOpen(false);
        void dispatch(fetchTemplatesAction({ websiteId: currentChannel }));
        toast({
          status: 'success',
          isClosable: true,
          duration: 10000,
          title: t(
            'manager.sections.templates.recontact_template.submit_success_title'
          ),
          description: t(
            'manager.sections.templates.recontact_template.pending'
          ),
        });
      } catch {
        toast({
          status: 'error',
          isClosable: true,
          title: t(
            'manager.sections.templates.recontact_template.submit_error_title'
          ),
          description: t(
            'manager.sections.templates.recontact_template.submit_error_description'
          ),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentChannel, dispatch, t, toast]
  );

  const value = useMemo(
    () => ({ state, isModalOpen, isSubmitting, openModal, closeModal, submit }),
    [state, isModalOpen, isSubmitting, openModal, closeModal, submit]
  );

  return (
    <RecontactTemplateContext.Provider value={value}>
      {children}
    </RecontactTemplateContext.Provider>
  );
};
